/**
 * @file PageManager.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Tuesday, 17th November 2020 9:14:27 am
 * @copyright 2015 - 2020 SKAT LLC, Delive LLC
 * @flow
 */

import type { ID } from 'web-panel-essentials/types'
import type { iPageManager, iLayout, Page, ToolbarButton } from '../../types'
import * as React from 'react'

import cn from 'classnames'
import { mix } from 'mixwith'
import mList from '../../utils/mList'
import shortid from 'shortid'
import { Inject, Injectable } from '../../serviceLocator'
import Sidebar from '../../views/Sidebar'
import Toolbar from '../../views/Toolbar'

class PagerView extends mix(React.Component).with(mList) implements iPageManager {
  constructor () {
    super()
    this.state = {
      pages: this.defineList('pages'),
      active: null,
      activated: new Set<ID>()
    }
  }

  _preAddPages (items) {
    return items.map((i) => {
      i.key = shortid()
      i.ref = React.createRef()
      return i
    })
  }

  _postAddPages (items) {
    return items.map(i => [i.ref])
  }

  activate ({ id }) : Promise<void> {
    return new Promise((resolve) => {
      const activated = this.state.activated
      activated.add(id)
      this.setState({ active: id, activated }, resolve)
    })
  }

  getActive () : Page {
    return this.state.pages.find(({ id }) => id === this.state.active)
  }

  getPageByName (pageName: string) : Page {
    return this.state.pages.find(({ name }) => name === pageName)
  }

  getPageById (pageId: ID) : Page {
    return this.state.pages.find(({ id }) => id === pageId)
  }

  render () : React.Node {
    const { active, pages, activated } = this.state
    return (
      <div className='pages'>
        {pages.map((page) => {
          const className = cn('page-body', { conceal: page.id !== active })
          if (activated.has(page.id)) {
            return (
              <div key={page.key} className={className}>
                {page.view ? React.cloneElement(page.view, { ref: page.ref, conceal: page.id !== active }) : null}
              </div>
            )
          } else return null
        })}
      </div>
    )
  }
}

/**
 * Proxy binding around PagerView
 * Responsible for the connection between
 * tabs, sidebar and toolbar.
 */
@Injectable('pageManager', true)
class PageManager implements iPageManager {
  @Inject layout : iLayout

  pagerView: React.ElementRef<any>
  sidebar: React.ElementRef<any>
  toolbar: React.ElementRef<any>

  constructor () {
    this.pagerView = this.layout.renderView(<PagerView />, 'main-region')
    this.sidebar = this.layout.renderView(
      <Sidebar onActivate={(button) => {
        this.pagerView.activate({ id: button.id })
        this.toolbar.changePage({ id: button.id })
      }}
      />, 'sidebar-region')
    this.toolbar = this.layout.renderView(<Toolbar />, 'buttons-region')
  }

  /**
   * Add a new page. Toolbar and sidebar will be injected automatically
   * @method addPage
   * @param {Object} page - page object
   * @param {String} page.name - page name
   * @param {String} page.label - label for sidebar button
   * @param {String} page.title - title for sidebar button, appears on mouse hover
   * @param {String} page.icon - icon name from fontAwesome catalogue, e.g. 'cirle', 'bug'...
   * @param {Object[]} page.actions - list of toolbar actions for current page
   * @param {String[]} page.actions[].name - action name
   * @param {String[]} page.actions[].action - action callback
   * @example
   * this.addPage({
    *   name: 'myPage',
    *   label: __('MY_PAGE'),
    *   title: __('USE_MY_PAGE'),
    *   icon: 'square',
    *   view: (<div>BELLLA</div>)
    *   //TODO: Add callbacks
    * })
    */
  async addPage ({ id = Symbol('page'), name, label, title, icon, actions, priority, view, testName } : Page) : Promise<Page> {
    this.sidebar.addButtons([{ id, name, label, title, priority, icon, testName }])
    this.addButtonsToPage(actions, id)
    const [page] = await this.pagerView.addPages([{ id, name, view }])
    return page
  }

  async addButtonsToPage (actions: Array<ToolbarButton>, pageId: ID) {
    await this.toolbar.addLocalButtons(actions.map((action) => ({ ...action, page: pageId })))
  }

  async updateButtons (actions: Array<Object>, pageId: ID) {
    this.toolbar.updateLocalButtons(actions.map((action) => ({ ...action, page: pageId })))
  }

  async highlightPage (...args: Array<any>) : Promise<void> {
    return this.sidebar.highlight(...args)
  }

  async activatePage (...args: Array<any>) : Promise<void> {
    await this.sidebar.activate(...args)
  }

  getPageByName (name: string) : Page {
    return this.pagerView.getPageByName(name)
  }

  getPageById (id: ID) : Page {
    return this.pagerView.getPageById(id)
  }

  getActive () : Page {
    return this.pagerView.getActive()
  }
}

export default PageManager
